/** 文生图生成记录：类型、解析与 URL 提取（供页面与全局 store 共用） */

/** 当前唯一支持组图（并行多图）的模型 */
export const IMAGE_GEN_GROUP_GEN_MODEL = 'gemini-2.5-flash-image';

export function supportsImageGenGroupMode(modelId: string): boolean {
  return modelId.trim() === IMAGE_GEN_GROUP_GEN_MODEL;
}

export const RESOLUTION_OPTIONS = ['1K', '2K', '4K'] as const;
export type ResolutionChoice = (typeof RESOLUTION_OPTIONS)[number];

export const ASPECT_OPTIONS = [
  '1:1',
  '1:4',
  '1:8',
  '2:3',
  '3:2',
  '3:4',
  '4:1',
  '4:3',
  '4:5',
  '5:4',
  '8:1',
  '9:16',
  '16:9',
  '21:9',
] as const;
export type AspectChoice = (typeof ASPECT_OPTIONS)[number];

/** 与请求里 referenceFiles 等价，写入本地以便重新编辑 / 重新生成还原参考图（多为 base64） */
export type GenRecordReferenceInput = {
  mime: string;
  /** 不含 data: 前缀的 base64 */
  base64: string;
};

/** 单条生成记录随行最多保留的参考图张数（与 GPT Image 2 上限对齐） */
export const GEN_RECORD_REFERENCE_INPUT_CAP = 8;

/** 写入 GenRecord 前规范化（裁剪、去掉空白与 data URL 前缀） */
export function normalizeReferenceInputsForPersist(
  refs: { base64: string; mime: string }[],
): GenRecordReferenceInput[] | undefined {
  const out: GenRecordReferenceInput[] = [];
  for (const r of refs) {
    if (out.length >= GEN_RECORD_REFERENCE_INPUT_CAP) break;
    const mime =
      typeof r.mime === 'string'
        ? r.mime.trim()
        : '';
    let b64 = typeof r.base64 === 'string' ? r.base64.replace(/\s/g, '') : '';
    const dataPrefix = /^data:[^;,]+;base64,/i;
    if (dataPrefix.test(b64)) {
      const m = /^data:([^;,]+);base64,(.*)$/i.exec(b64);
      if (m) {
        b64 = m[2]?.replace(/\s/g, '') ?? '';
      }
    }
    if (!mime || !b64) continue;
    out.push({ mime, base64: b64 });
  }
  return out.length ? out : undefined;
}

export function referenceInputsToRunPayload(
  inputs: GenRecordReferenceInput[] | undefined | null,
): { base64: string; mime: string }[] {
  if (!inputs?.length) return [];
  const out: { base64: string; mime: string }[] = [];
  for (const x of inputs) {
    const mime = typeof x.mime === 'string' ? x.mime.trim() : '';
    const b64 =
      typeof x.base64 === 'string' ? x.base64.replace(/\s/g, '') : '';
    if (!mime || !b64) continue;
    out.push({ base64: b64, mime });
  }
  return out;
}

export type GenRecord = {
  id: string;
  model: string;
  prompt: string;
  images: string[];
  createdAt: number;
  resolution: ResolutionChoice;
  aspect: AspectChoice;
  imageSize?: string;
  /** 生成完全失败时的错误摘要（persist 后可以重新编辑 / 重新生成） */
  generationError?: string;
  /** 图生图时使用的参考图，用于恢复底栏后再编辑 / 再生 */
  referenceInputs?: GenRecordReferenceInput[];
  /** 生成方式（组图失败时 images 为空，仍需靠此字段还原） */
  genMode?: 'single' | 'group';
  groupCount?: number;
};

export type GeneratingPlaceholder = {
  id: string;
  model: string;
  prompt: string;
  resolution: ResolutionChoice;
  aspect: AspectChoice;
  createdAt: number;
  batchCount?: number;
  imageSize?: string;
  batchParallel?: boolean;
};

export const IMAGE_GEN_CACHE_MAX = 80;
/** 达到该条数后，每次新增生成记录会先清理最久的一条以预留空间 */
export const IMAGE_GEN_CACHE_SOFT_LIMIT = IMAGE_GEN_CACHE_MAX - 5;
export const IMAGE_GEN_RETENTION_MS = 14 * 24 * 60 * 60 * 1000;

const OPENAI_GPT_IMAGE_MODELS = ['gpt-image-1', 'gpt-image-2'] as const;
const openAiGptImageModelSet = new Set<string>(OPENAI_GPT_IMAGE_MODELS);

const allStoredOpenAiImageSizes = new Set<string>([
  '1024x1024',
  '1536x1024',
  '1024x1536',
  'auto',
  '1536x864',
  '2560x1440',
  '3840x2160',
]);

export const GPT_IMAGE_1_MODEL = 'gpt-image-1';
export const GPT_IMAGE_2_MODEL = 'gpt-image-2';

export function isOpenAiGptImageModel(modelId: string): boolean {
  return openAiGptImageModelSet.has(modelId.trim());
}

/** 仅 GPT Image 2 支持 `/v1/images/edits` 图生图 */
export function supportsOpenAiGptImageEdit(modelId: string): boolean {
  return modelId.trim() === GPT_IMAGE_2_MODEL;
}

function isValidOpenAiImageSizeForModel(modelId: string, size: string): boolean {
  if (!isOpenAiGptImageModel(modelId)) return false;
  if (modelId.trim() === GPT_IMAGE_1_MODEL) {
    return ['1024x1024', '1536x1024', '1024x1536', 'auto'].includes(size);
  }
  return allStoredOpenAiImageSizes.has(size);
}

function sortGenerationsNewestFirst(list: GenRecord[]): GenRecord[] {
  return [...list].sort((a, b) => b.createdAt - a.createdAt);
}

export type TrimGenerationsCacheResult = {
  list: GenRecord[];
  evictedByCap: number;
  evictedByRetention: number;
};

/** 按保留期与条数上限裁剪；justAppended 为 true 时若超过软上限会先删最久一条 */
export function trimGenerationsForCacheLimit(
  list: GenRecord[],
  options?: { justAppended?: boolean },
): TrimGenerationsCacheResult {
  const cutoff = Date.now() - IMAGE_GEN_RETENTION_MS;
  const sorted = sortGenerationsNewestFirst(list);

  const afterRetention: GenRecord[] = [];
  let evictedByRetention = 0;
  for (const r of sorted) {
    if (typeof r.createdAt === 'number' && r.createdAt >= cutoff) {
      afterRetention.push(r);
    } else {
      evictedByRetention += 1;
    }
  }

  let next = afterRetention;
  let evictedByCap = 0;

  if (options?.justAppended && next.length > IMAGE_GEN_CACHE_SOFT_LIMIT && next.length > 0) {
    next = next.slice(0, -1);
    evictedByCap += 1;
  }

  while (next.length > IMAGE_GEN_CACHE_MAX && next.length > 0) {
    next = next.slice(0, -1);
    evictedByCap += 1;
  }

  return { list: next, evictedByCap, evictedByRetention };
}

export function clipGenerationsToRetentionAndCap(list: GenRecord[]): GenRecord[] {
  return trimGenerationsForCacheLimit(list).list;
}

export function parseStoredGenerations(raw: string): GenRecord[] {
  if (!raw.trim()) return [];
  try {
    const parsed = JSON.parse(raw) as unknown;
    if (!Array.isArray(parsed)) return [];
    const aspectOk = new Set<string>([...ASPECT_OPTIONS]);
    const resolutionOk = new Set<string>([...RESOLUTION_OPTIONS]);
    const out: GenRecord[] = [];
    for (const x of parsed) {
      if (!x || typeof x !== 'object') continue;
      const o = x as Record<string, unknown>;
      const id = o.id;
      const prompt = o.prompt;
      const model = o.model;
      const createdAt = o.createdAt;
      const images = o.images;
      const resolutionRaw = o.resolution;
      const tierLegacy = o.tier;
      const aspect = o.aspect;
      if (
        typeof id !== 'string' ||
        typeof prompt !== 'string' ||
        typeof model !== 'string' ||
        typeof createdAt !== 'number' ||
        !Array.isArray(images) ||
        !images.every((u) => typeof u === 'string')
      ) {
        continue;
      }
      let resolution: ResolutionChoice = '2K';
      if (typeof resolutionRaw === 'string') {
        const normalized = resolutionRaw === '512' ? '1K' : resolutionRaw;
        if (resolutionOk.has(normalized)) {
          resolution = normalized as ResolutionChoice;
        }
      } else if (tierLegacy === '4K') {
        resolution = '4K';
      } else if (tierLegacy === '2K') {
        resolution = '2K';
      }
      const imageSizeRaw = o.imageSize;
      let imageSize: string | undefined;
      if (
        typeof imageSizeRaw === 'string' &&
        isValidOpenAiImageSizeForModel(model, imageSizeRaw)
      ) {
        imageSize = imageSizeRaw;
      }
      const generationErrorRaw = o.generationError;
      const generationError =
        typeof generationErrorRaw === 'string' && generationErrorRaw.trim()
          ? generationErrorRaw.trim()
          : undefined;

      let referenceInputs: GenRecordReferenceInput[] | undefined;
      const refInRaw = o.referenceInputs;
      if (Array.isArray(refInRaw)) {
        const acc: GenRecordReferenceInput[] = [];
        for (const item of refInRaw.slice(0, GEN_RECORD_REFERENCE_INPUT_CAP)) {
          if (!item || typeof item !== 'object') continue;
          const rr = item as Record<string, unknown>;
          const m = rr.mime;
          const b64r = rr.base64;
          if (typeof m !== 'string' || typeof b64r !== 'string') continue;
          const mt = m.trim();
          let b64 = b64r.replace(/\s/g, '');
          const dataPx = /^data:[^;,]+;base64,/i;
          if (dataPx.test(b64)) {
            const mm = /^data:([^;,]+);base64,(.*)$/i.exec(b64);
            if (mm) b64 = (mm[2] ?? '').replace(/\s/g, '');
          }
          if (!mt || !b64) continue;
          acc.push({ mime: mt, base64: b64 });
        }
        if (acc.length) referenceInputs = acc;
      }

      const genModeRaw = o.genMode;
      const genMode =
        genModeRaw === 'single' || genModeRaw === 'group' ? genModeRaw : undefined;
      const groupCountRaw = o.groupCount;
      const groupCount =
        typeof groupCountRaw === 'number' &&
        Number.isFinite(groupCountRaw) &&
        groupCountRaw >= 2 &&
        groupCountRaw <= 4
          ? Math.round(groupCountRaw)
          : undefined;

      out.push({
        id,
        model,
        prompt,
        images: images as string[],
        createdAt,
        resolution,
        aspect: (typeof aspect === 'string' && aspectOk.has(aspect)
          ? aspect
          : '16:9') as AspectChoice,
        ...(imageSize ? { imageSize } : {}),
        ...(generationError ? { generationError } : {}),
        ...(referenceInputs?.length ? { referenceInputs } : {}),
        ...(genMode ? { genMode } : {}),
        ...(groupCount != null ? { groupCount } : {}),
      });
    }
    return out;
  } catch {
    return [];
  }
}

export const extractImageUrlsFromUnknown = (result: unknown): string[] => {
  const out: string[] = [];
  const seen = new Set<string>();

  const push = (s: string) => {
    const t = s.trim();
    if (!t || seen.has(t)) return;
    seen.add(t);
    out.push(t);
  };

  const walk = (node: unknown, depth: number) => {
    if (depth > 22 || node == null) return;

    if (typeof node === 'string') {
      if (/^https?:\/\//i.test(node)) push(node);
      return;
    }

    if (Array.isArray(node)) {
      for (const x of node) walk(x, depth + 1);
      return;
    }

    if (typeof node !== 'object') return;
    const o = node as Record<string, unknown>;

    const inlineRoot = (o.inline_data ?? o.inlineData) as Record<string, unknown> | undefined;
    if (inlineRoot && typeof inlineRoot.data === 'string') {
      const mime = String(inlineRoot.mime_type ?? inlineRoot.mimeType ?? 'image/png');
      push(`data:${mime};base64,${inlineRoot.data}`);
    }

    const url = o.url;
    if (typeof url === 'string' && /^https?:\/\//i.test(url)) push(url);

    const imageUrl = o.image_url;
    if (typeof imageUrl === 'string' && /^https?:\/\//i.test(imageUrl)) push(imageUrl);

    const b64 = o.b64_json;
    if (typeof b64 === 'string' && b64.length > 0) push(`data:image/png;base64,${b64}`);

    for (const k of Object.keys(o)) {
      walk(o[k], depth + 1);
    }
  };

  const payload =
    result && typeof result === 'object' && 'data' in result && (result as { data: unknown }).data != null
      ? (result as { data: unknown }).data
      : result;
  walk(payload, 0);
  return out;
};
